import { 
  generateTimeSlotsFromRange, 
  generateTimeSlotsFromWorkingHours,
  filterPastTimeSlotsEST, 
  sortTimeSlotsEST 
} from './timeSlotUtils';
import type { FranchiseData } from './franchiseStorage';

/**
 * Reusable booking logic utilities
 * Extracted from BookingPage for better maintainability and testability
 */

export interface Service {
  id: string;
  name: string;
  description: string;
  price: string;
  duration: number;
  image: string;
  category: string;
  availableStylists: string[];
}

export interface Stylist {
  id: string;
  name: string;
  role: string;
  image: string;
  description: string;
  services: string[];
  availability: {
    [key: string]: string[];
  };
}

export interface BookingData {
  service?: Service;
  stylist?: Stylist;
  date?: string;
  time?: string;
}

/**
 * Filter stylists by selected service
 * Returns only stylists who can perform the selected service
 */
export const getAvailableStylistsForService = (
  service: Service | undefined,
  allStylists: Stylist[]
): Stylist[] => {
  if (!service) return allStylists;
  return allStylists.filter(stylist => 
    stylist.services.includes(service.id)
  );
};

/**
 * Filter services by selected stylist and category
 * Returns services that the stylist can perform, filtered by category
 */
export const getAvailableServicesForStylist = (
  stylist: Stylist | undefined,
  allServices: Service[],
  selectedCategory: string
): Service[] => {
  let filtered = allServices;
  
  // Filter by stylist if selected
  if (stylist) {
    filtered = filtered.filter(service => 
      stylist.services.includes(service.id)
    );
  }
  
  // Filter by category
  if (selectedCategory !== 'all') {
    filtered = filtered.filter(service => 
      service.category === selectedCategory
    );
  }
  
  return filtered;
};

/**
 * Get available time slots for a given date, stylist, and franchise
 * Priority order:
 * 1. If stylist selected - use their availability
 * 2. If franchise provided - use franchise working hours
 * 3. Otherwise - default 9 AM - 5 PM slots
 * Filters out past times based on EST timezone
 */
export const getAvailableTimeSlots = (
  stylist: Stylist | undefined,
  selectedDate: Date,
  franchise?: FranchiseData | null
): string[] => {
  const dayName = selectedDate
    .toLocaleDateString('en-US', { weekday: 'long' })
    .toLowerCase();
  
  let slots: string[] | null = [];
  
  if (stylist) {
    // Priority 1: Stylist selected - use their availability
    const ranges = stylist.availability[dayName] || [];
    slots = ranges.flatMap(range => generateTimeSlotsFromRange(range));
  } else if (franchise?.working_hours) {
    // Priority 2: Use franchise working hours
    slots = generateTimeSlotsFromWorkingHours(
      franchise.working_hours,
      selectedDate,
      30 // 30-minute intervals
    );
    
    // If franchise is closed, return empty array
    if (slots === null) {
      return [];
    }
  } else {
    // No franchise data available - return empty array
    return [];
  }
  
  // Filter past times and sort
  return filterPastTimeSlotsEST(sortTimeSlotsEST(slots), selectedDate);
};

/**
 * Validate if user can proceed to next step
 * Different validation rules based on entry point
 */
export const canProceedToNextStep = (
  entryPoint: 'service' | 'stylist',
  bookingData: BookingData
): boolean => {
  if (entryPoint === 'stylist') {
    // Stylist-first: Need service + time
    return !!bookingData.service && !!bookingData.time;
  } else {
    // Service-first: Need only time (stylist optional)
    return !!bookingData.time;
  }
};

/**
 * Check if booking data is complete for final submission
 */
export const isBookingDataComplete = (bookingData: BookingData): boolean => {
  return !!(
    bookingData.service &&
    bookingData.date &&
    bookingData.time
  );
};

/**
 * Generate a unique booking reference ID
 */
export const generateBookingReference = (
  date: string,
  time: string,
  serviceId: string
): string => {
  const timestamp = new Date().getTime();
  return `${date}_${time}_${serviceId}_${timestamp}`.replace(/[^a-zA-Z0-9_]/g, '_');
};
